import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Box,
  Heading,
  Image,
  AspectRatio,
  Text,
  Divider,
  HStack,
  Tag,
  useColorModeValue,
  Container,
  SimpleGrid,
} from '@chakra-ui/react';
import { Helmet } from 'react-helmet';
import api from 'api/axiosConfig';
import API_BASE from "configs/hostConfig";
import { cleanHtmlContent } from 'utils/HtmlContentUtils';
import { getImageUrl } from 'utils/ImageImporter';
import { DynivyLogo } from 'components/Logo';

const BlogTags = (props) => {
  const { marginTop = 0, tags } = props;

  return (
    <HStack spacing={2} marginTop={marginTop}>
      {tags?.map((tag) => {
        return (
          <Tag size={'md'} colorScheme="green" key={tag}>
            {tag}
          </Tag>
        );
      })}
    </HStack>
  );
};
BlogTags.propTypes = {
  marginTop: PropTypes.number,
  tags: PropTypes.array,
};

const BlogAuthor = ({ name, date }) => {
  let author = name;
  if (!name || name === 'dynivy') {
    author = 'Dynivy Academy';
  }

  return (
    <HStack marginTop="2" spacing="2" display="flex" alignItems="center">
      <DynivyLogo logoType="logo" wSize="24px" />
      <Text fontWeight="medium">{author}</Text>
      <Text>—</Text>
      <Text>{new Date(date).toLocaleDateString()}</Text>
    </HStack>
  );
};

BlogAuthor.propTypes = {
  name: PropTypes.string,
  date: PropTypes.string,
};

function BlogListPage() {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [featuredBlog, setFeaturedBlog] = useState([]);

  const updateFeaturedBlogs = (featuredBlogs, allBlogs) => {
    if (featuredBlogs && featuredBlogs.length > 0) {
      setFeaturedBlog(featuredBlogs[0]);
    } else if (allBlogs && allBlogs.length > 0) {
      setFeaturedBlog(allBlogs[0]);
    }
  };

  useEffect(() => {
    api
      .get(`${API_BASE}/api/blog/list`)
      .then((res) => {
        // console.log('============= fetch blog list response: ', res);
        if (res.status == 200) {
          setBlogs(res.data?.blogs);
          updateFeaturedBlogs(
            res.data?.featuredBlogs,
            res.data?.blogs
          );
        }
      })
      .catch(() => console.log('failed to fetch blog list data'));
  }, []);

  return (
    <>
      <Helmet>
        <title>Our Blogs | Dynivy</title>
        <meta name="title" content="Dynivy - Our Blogs" />
        <meta
          name="description"
          content="Read about our journey and writing resources from Dynivy Academy"
        />
      </Helmet>
      <Container maxW={'6xl'} p="12" mt={12}>
        <Heading as="h1">Stories by Dynivy Academy</Heading>
        {featuredBlog.blogId && (
          <Box
            marginTop={{ base: '1', sm: '5' }}
            display="flex"
            flexDirection={{ base: 'column', md: 'row' }}
            justifyContent="space-between"
          >
            <Box
              display="flex"
              flex="1"
              marginRight="3"
              position="relative"
              alignItems="center"
            >
              <Box
                width={{ base: '100%', sm: '85%' }}
                zIndex="2"
                marginLeft={{ base: '0', sm: '5%' }}
                marginTop="5%"
                marginBottom="5%"
              >
                <Box textDecoration="none" _hover={{ textDecoration: 'none' }}>
                  <Image
                    borderRadius="lg"
                    src={getImageUrl(featuredBlog.imageUrl)}
                    alt={`imge for blog with title ${featuredBlog.title}`}
                    objectFit="contain"
                  />
                </Box>
              </Box>
              <Box zIndex="1" width="100%" position="absolute" height="100%">
                <Box
                  bgGradient={useColorModeValue(
                    'radial(green.600 1px, transparent 1px)',
                    'radial(geen.300 1px, transparent 1px)'
                  )}
                  backgroundSize="20px 20px"
                  opacity="0.4"
                  height="100%"
                />
              </Box>
            </Box>
            <Box
              display="flex"
              flex="1"
              flexDirection="column"
              justifyContent="center"
              marginTop={{ base: '3', sm: '0' }}
            >
              <BlogTags tags={featuredBlog.tags} />
              <Heading marginTop="1">
                <Text textDecoration="none" _hover={{ textDecoration: 'none' }}>
                  {featuredBlog.title}
                </Text>
              </Heading>
              <Box>
                <Text
                  as="p"
                  marginTop="2"
                  color={useColorModeValue('gray.700', 'gray.200')}
                  fontSize="lg"
                  display="-webkit-box"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  css={{
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 5,
                  }}
                >
                  {cleanHtmlContent(featuredBlog.content)}
                </Text>
                <Button
                  size="md"
                  marginTop="1"
                  variant="link"
                  colorScheme="blue"
                  onClick={() => navigate(`/blog/${featuredBlog.slug}`)}
                >
                  Read Full Story
                </Button>
              </Box>

              <BlogAuthor
                name={featuredBlog.author}
                date={featuredBlog.updatedTime}
              />
            </Box>
          </Box>
        )}

        <Heading as="h2" marginTop="5">
          Latest articles
        </Heading>
        <Divider marginTop="5" />
        <Container maxW={'6xl'} mt={2}>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={10}>
            {blogs?.map((blog) => (
              <Box key={blog.blogId} w="100%">
                <Box borderRadius="lg" overflow="hidden">
                  <Box
                    textDecoration="none"
                    _hover={{ textDecoration: 'none' }}
                  >
                    <AspectRatio ratio={16 / 9} width="100%">
                      <Image
                        transform="scale(1.0)"
                        src={getImageUrl(blog.imageUrl || '')}
                        alt={`imge for blog with title ${blog.title}`}
                        objectFit="contain"
                        // width="100%"
                        transition="0.3s ease-in-out"
                        _hover={{
                          transform: 'scale(1.05)',
                        }}
                      />
                    </AspectRatio>
                  </Box>
                </Box>
                <BlogTags tags={blog.tags} marginTop={3} />
                <Heading fontSize="xl" marginTop="2">
                  <Text
                    textDecoration="none"
                    _hover={{ textDecoration: 'none' }}
                  >
                    {blog.title}
                  </Text>
                </Heading>
                <Box>
                  <Text
                    as="p"
                    marginTop="2"
                    color={useColorModeValue('gray.700', 'gray.200')}
                    fontSize="md"
                    display="-webkit-box"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    css={{
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 3,
                    }}
                  >
                    {cleanHtmlContent(blog.content)}
                  </Text>
                  <Button
                    size="md"
                    marginTop="1"
                    variant="link"
                    colorScheme="blue"
                    onClick={() => navigate(`/blog/${blog.slug}`)}
                  >
                    Read Full Story
                  </Button>
                </Box>
                <BlogAuthor name={blog.author} date={blog.updatedTime} />
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Container>
    </>
  );
}

export default BlogListPage;
